import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  ArrowRightIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  PlusCircleIcon,
  TruckIcon,
  UserGroupIcon,
  UserMinusIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { format, isSameDay } from "date-fns";
import RoleOnly, { useUserHasAccess } from "../../routes/roleOnly";
import { useSettings } from "../../contexts/settings-context";
import { DashboardSetupOverview } from "../../components/dashboard/home/DashboardSetupOverview";
import { DashboardWeekHours, fetchWeekHours } from "../../components/dashboard/home/DashboardWeekHours";
import "../../components/dashboard/home/dashboard-design.css";
import CurrentPhoneSection from "./phone/currentPhoneSection";
import { HourForm } from "../../components/dashboard/hours/hour_form";
import { AbsenceForm } from "../../components/dashboard/absence/absence_form";
import { PMDialog } from "../../components/utils/PMDialog";
import { ReportHoursTag } from "../../components/dashboard/service/reports/report-hours-tag";
import { ItemListProductStatusTag } from "../../components/dashboard/service/reports/item-list-product-status-tag";
import { fetchProject } from "../../api/project";
import { fetchWorkOrder } from "../../api/work-order";
import { fetchUsers } from "../../api/users";
import { resolveColleagueContact } from "../../components/utils/ColleagueDialog";
import axios from "../../api/axios";
import { getMyReadRequirements, HSE_READ_CHANGED_EVENT } from "../../api/hse/documentCompliance";
import { HomeReadingCard } from "../../components/dashboard/hse/compliance/HomeReadingCard";
import { toast } from "react-hot-toast";
import { eventPath, formatEventLocalTimeRange, getEventColor, getEventIcon } from "./calendar/utils";
import { ItemStatus, status } from "../../components/Utils";
import { ReportSimpleAgreementStatus } from "../../components/dashboard/service/reports/utils";
import { DashboardFollowup } from "../../components/dashboard/home/DashboardFollowup";
import { AccountingSummarySection } from "../../components/dashboard/accounting/AccountingSummarySection";
import { HseEventForm } from "../../components/dashboard/hse/hseEventForm";
import { fetchDrivingDriverByUser } from "../../api/driving/driver";
import { driverVehicleAction } from "./driving/drivers/utils";
import VehiclesListDialog from "./vehicles/list-dialog";
import { EmployeeToolsDialog } from "../../components/dashboard/tools/EmployeeToolsDialog";
import ReportGuideOnboardingDialog from "../../components/dashboard/service/reports/ReportGuideOnboardingDialog";
import {
  createReportGuideRun,
  fetchReportGuideTemplates,
} from "../../api/reportGuides";
import { excludingPromangStockReportGuideTemplates } from "../../utils/promangStockReportGuide";
import { useGeneralNavMenuPageActions } from "../../components/dashboard/general-nav-menu";
import { HomeStatusComposer } from "../../components/dashboard/statusUpdates/HomeStatusComposer";
import { intlLocaleFor } from "../../i18n/languages";

const todayString = () => format(new Date(), "yyyy-MM-dd");

const moduleLabels = {
  report: "Serviceordre",
  work_order: "Arbeidsordre",
  project_plan: "Planlagt aktivitet",
  project: "Prosjekt",
  order: "Ordre",
  case: "Sak",
  absence: "Fravær",
};

const getWorkingDayInfo = (event, date) => {
  const schedule = event?.working_hours?.schedule;
  if (!Array.isArray(schedule)) return null;
  return schedule.find((entry) => entry?.date === date) || null;
};

const getEventTimeLabel = (event, date) => {
  const workingDayInfo = getWorkingDayInfo(event, date);

  if (workingDayInfo && !workingDayInfo.day_off) {
    const hours =
      workingDayInfo.hours === null || workingDayInfo.hours === undefined
        ? null
        : Number(workingDayInfo.hours);

    return `${workingDayInfo.start_time} - ${workingDayInfo.end_time}${
      Number.isFinite(hours) ? ` (${hours} t)` : ""
    }`;
  }

  if (event?.start_time && event?.end_time) {
    return formatEventLocalTimeRange(event.start_time, event.end_time);
  }

  return "";
};

const getEventMetaRows = (event) => {
  const rows = [];

  if (event?.module === "report" && event?.module_details?.report_number) {
    rows.push(`Rapport #${event.module_details.report_number}`);
  }

  if (event?.module === "work_order" && event?.module_details?.work_order_number) {
    rows.push(`Arbeidsordre #${event.module_details.work_order_number}`);
  }

  if (event?.module === "project_plan" && event?.module_details?.project_activity) {
    rows.push(
      event.module_details.project_activity.display_title ||
        event.module_details.project_activity.title
    );
  }

  if (event?.module_details?.customer?.displayTitle) {
    rows.push(event.module_details.customer.displayTitle);
  } else if (event?.module_details?.customer) {
    rows.push(event.module_details.customer);
  }

  if (event?.module_details?.project?.displayTitle) {
    rows.push(event.module_details.project.displayTitle);
  } else if (event?.module_details?.project?.name) {
    rows.push(event.module_details.project.name);
  }

  if (event?.location) {
    rows.push(event.location);
  }

  return rows.filter(Boolean);
};

const getPlannedHours = (event, date) => {
  const workingDayInfo = getWorkingDayInfo(event, date);
  if (workingDayInfo && !workingDayInfo.day_off) {
    return Number(workingDayInfo.hours) || 0;
  }

  if (event?.module_details?.total_hours !== undefined) {
    return Number(event.module_details.total_hours) || 0;
  }

  if (event?.start_time && event?.end_time) {
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    const durationMs = end.getTime() - start.getTime();

    if (Number.isFinite(durationMs) && durationMs > 0) {
      return durationMs / (1000 * 60 * 60);
    }
  }

  return 0;
};

/** Build hour-form prefill from a dashboard/calendar event (no description). */
const buildHourPrefillFromEvent = async (event, selectedDate, userId) => {
  const details = event?.module_details || {};

  let projectId = event?.project_id ?? null;
  let project = null;
  const rawProject = details.project ?? event?.project;

  if (rawProject && typeof rawProject === "object" && rawProject.id != null) {
    projectId = rawProject.id;
    project = rawProject;
  } else if (typeof rawProject === "string" && rawProject.trim()) {
    project = projectId
      ? { id: projectId, displayTitle: rawProject, name: rawProject }
      : null;
  } else if (projectId) {
    project = { id: projectId };
  }

  let projectActivity =
    details.project_activity ||
    (Array.isArray(details.project_activities)
      ? details.project_activities.find((a) => a.is_primary) || details.project_activities[0]
      : null) ||
    null;

  if (event?.module === "work_order" && event?.module_id) {
    try {
      const res = await fetchWorkOrder(event.module_id, {
        fields: "id,project_id,project,project_activity_id,project_activity",
      });
      const wo = res?.data;
      if (wo?.project?.id) {
        projectId = wo.project_id ?? wo.project.id;
        project = wo.project;
      } else if (wo?.project_id && !project?.displayTitle) {
        projectId = wo.project_id;
      }
      if (wo?.project_activity?.id) {
        projectActivity = wo.project_activity;
      } else if (wo?.project_activity_id) {
        projectActivity = { id: wo.project_activity_id };
      }
    } catch (_error) {
      // Keep event-derived fallbacks
    }
  }

  if (projectId && project && !project.displayTitle && !project.name) {
    try {
      const res = await fetchProject(projectId, {
        fields: "id,displayTitle,name,project_number,customer",
      });
      if (res?.data) project = res.data;
    } catch (_error) {
      // Keep stub project
    }
  }

  const prefill = {
    user_id: userId,
    project_id: projectId,
    project: project || (projectId ? { id: projectId } : undefined),
    project_activity_id: projectActivity?.id ?? null,
    project_activity: projectActivity || null,
    date: selectedDate,
  };

  if (event?.module === "report" && event?.module_id) {
    prefill.report_id = event.module_id;
  }

  if (event?.module === "work_order" && event?.module_id) {
    prefill.work_order_id = event.module_id;
  }

  const planned = getPlannedHours(event, selectedDate);
  if (planned > 0) {
    prefill.time = String(planned);
  }

  return prefill;
};

const getDisplayStatus = (event) => {
  if (!event) return "Planlagt";

  if (
    (event.module === "report" || event.module === "work_order") &&
    event?.module_details?.status
  ) {
    return event.module_details.status;
  }

  return event.status || "Planlagt";
};

/** Status chips matching calendar day view (`RenderDayView`): `ItemStatus` + avtale for serviceordre. */
const EventStatusChips = ({ event }) => {
  const { t } = useTranslation();
  const item = event?.module_details || event;
  const statusVal = item?.status ?? event?.status;
  const mapped =
    statusVal != null && typeof statusVal !== "object" && Boolean(status[statusVal]);
  const rawLabel = getDisplayStatus(event);
  const fallbackLabel =
    rawLabel === "Planlagt" ? t("dashboard_report.status.planned") : rawLabel;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {mapped ? (
        <ItemStatus item={item} />
      ) : (
        <span className="inline-flex rounded-full bg-slate-100 px-2 text-xs font-semibold leading-5 text-slate-700">
          {fallbackLabel}
        </span>
      )}
      {event?.module === "report" ? (
        <ReportSimpleAgreementStatus
          isApproved={event?.module_details?.appointment_confirmed}
        />
      ) : null}
    </div>
  );
};

const ActionLink = ({ to, icon: Icon, title, subtitle }) => (
  <Link
    to={to}
    className="pd-action-link"
  >
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">{subtitle}</div>
    </div>
  </Link>
);

const ActionButton = ({ onClick, icon: Icon, title, subtitle }) => (
  <button
    type="button"
    onClick={onClick}
    className="pd-action-link"
  >
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">{subtitle}</div>
    </div>
  </button>
);

const SideSection = ({ title, description, children }) => (
  <section className="pd-card pd-side-section">
    <div className="mb-4">
      <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </h2>
      {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

export const DashboardReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const canUseHours = useUserHasAccess({ role: "hours" });
  const canCreateWorkOrder = useUserHasAccess({ role: "projects_work-orders", action: "create" });
  const canReadEconomy = useUserHasAccess({ role: "admin" });
  const [dayFilter, setDayFilter] = useState("all");
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const currentUser = useSelector((state) => state.session.currentUser);

  const [selectedDate, setSelectedDate] = useState(todayString());
  const [eventsState, setEventsState] = useState({ isLoading: false, data: [] });
  const [dayHours, setDayHours] = useState([]);
  const [hoursState, setHoursState] = useState({ loading: true, error: false });
  const [hourFormId, setHourFormId] = useState(null);
  const [lastChangedHour, setLastChangedHour] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [absencePrefill, setAbsencePrefill] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [colleaguesOpen, setColleaguesOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehiclesListDialogOpen, setVehiclesListDialogOpen] = useState(false);
  const [reportGuideState, setReportGuideState] = useState({
    open: false,
    template: null,
    report: null,
    isSaving: false,
  });
  const [reportGuideChooserState, setReportGuideChooserState] = useState({
    open: false,
    report: null,
    templates: [],
  });
  const [colleaguesState, setColleaguesState] = useState({
    isLoading: false,
    data: [],
  });
  const [driverState, setDriverState] = useState({
    isLoading: false,
    driver: null,
    vehicle: null,
  });
  const [myReading, setMyReading] = useState(null);

  useEffect(() => {
    document.title = "Dashboard Report | Promang";
  }, []);

  // Own required HMS reading (same source as the floating reminder); refreshes after confirm/postpone.
  useEffect(() => {
    // Never show the previous company's reading while the new one loads.
    setMyReading(null);
    if (!currentUser?.id) return undefined;
    let cancelled = false;
    const load = () =>
      getMyReadRequirements()
        .then((data) => !cancelled && setMyReading(data))
        .catch(() => !cancelled && setMyReading(null));
    load();
    window.addEventListener(HSE_READ_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(HSE_READ_CHANGED_EVENT, load);
    };
  }, [currentUser?.id, currentCompany?.id]);

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    setEventsState({ isLoading: true, data: [], error: false });
    const params = new URLSearchParams({ date: selectedDate, view: "day", users: String(currentUser.id) });
    axios.get(`/events?${params}`).then((response) => {
      if (!Array.isArray(response?.data)) throw new Error("Invalid agenda response");
      if (!cancelled) setEventsState({ isLoading: false, data: response.data, error: false });
    }).catch(() => {
      if (!cancelled) setEventsState({ isLoading: false, data: [], error: true });
    });
    return () => { cancelled = true; };
  }, [selectedDate, currentUser?.id, currentCompany?.id, refreshTick]);

  useEffect(() => {
    if (!currentUser?.id || !canUseHours) return;
    let cancelled = false;
    setDayHours([]);
    setHoursState({ loading: true, error: false });
    fetchWeekHours(currentUser.id, selectedDate, selectedDate)
      .then((hours) => {
        if (cancelled) return;
        setDayHours(hours);
        setHoursState({ loading: false, error: false });
      }).catch(() => { if (!cancelled) setHoursState({ loading: false, error: true }); });
    return () => { cancelled = true; };
  }, [selectedDate, currentUser?.id, currentCompany?.id, canUseHours, lastChangedHour?.id, refreshTick]);

  useEffect(() => {
    if (!colleaguesOpen) return;
    if (colleaguesState.data.length > 0) return;

    const run = async () => {
      setColleaguesState({ isLoading: true, data: [] });
      try {
        const response = await fetchUsers({
          filters: [{ name: "EMPLOYEE", value: true }],
          page: 1,
          per_page: 40,
          query: "",
          sort: "asc",
          sortBy: "name",
          view: "all",
          fields:
            "id,displayTitle,name,email,mobile,mobile_work,user_next_of_kins,employment.id,employment.email,employment.mobile,employment.phone",
        });
        setColleaguesState({
          isLoading: false,
          data: Array.isArray(response?.data) ? response.data : [],
        });
      } catch (_error) {
        setColleaguesState({ isLoading: false, data: [] });
      }
    };

    run();
  }, [colleaguesOpen, colleaguesState.data.length]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const run = async () => {
      setDriverState((state) => ({ ...state, isLoading: true }));
      try {
        const response = await fetchDrivingDriverByUser({
          user_id: currentUser.id,
          fields:
            "id,displayTitle,checked_into_vehicle.id,checked_into_vehicle.displayTitle,checked_into_vehicle.number_plate,checked_into_vehicle.distance_driven_today,checked_into_vehicle.distance_driven_this_week,checked_into_vehicle.distance_driven_this_month",
        });

        const driver = response?.data?.id ? response.data : null;
        setDriverState({
          isLoading: false,
          driver,
          vehicle: driver?.checked_into_vehicle || null,
        });
      } catch (_error) {
        setDriverState({
          isLoading: false,
          driver: null,
          vehicle: null,
        });
      }
    };

    run();
  }, [currentUser?.id, refreshTick]);

  const visibleEvents = (Array.isArray(eventsState.data) ? eventsState.data : [])
    .filter((event) => {
      const workingDayInfo = getWorkingDayInfo(event, selectedDate);
      return !(workingDayInfo && workingDayInfo.day_off);
    })
    .sort((left, right) => {
      const leftTime = new Date(left.start_time).getTime();
      const rightTime = new Date(right.start_time).getTime();
      return leftTime - rightTime;
    });

  const filteredEvents = visibleEvents.filter((event) => dayFilter === "all" || event.module === dayFilter);

  const totalHoursToday = (dayHours || []).reduce(
    (sum, hour) => sum + (Number(String(hour?.time ?? 0).replace(",", ".")) || 0),
    0
  );

  const plannedHoursToday = visibleEvents.reduce(
    (sum, event) => sum + getPlannedHours(event, selectedDate),
    0
  );

  const reportCount = visibleEvents.filter((event) => event.module === "report").length;
  const workOrderCount = visibleEvents.filter((event) => event.module === "work_order").length;

  const isPhoneroConfigured =
    currentCompany?.phonero_username?.trim() &&
    currentCompany?.phonero_password?.trim();

  const vehicle = driverState.vehicle;
  const driver = driverState.driver;
  const automileCheckinFilter = Boolean(currentCompany?.automile_ready);
  const checkInVehicleFilters = [
    { name: "active", value: true },
    { name: "available_for_driver_checkin", value: true },
    ...(automileCheckinFilter ? [{ name: "synced_with_automile", value: true }] : []),
  ];

  const formatHours = (value) => {
    if (value === null || value === undefined) return "0.00";
    const number = Number(typeof value === "string" ? value.replace(",", ".") : value);
    return Number.isNaN(number) ? "0.00" : number.toFixed(2);
  };

  const onSelectEvent = async (event) => {
    const isUsed = (dayHours || []).some((hour) => {
      const sameDay = hour?.date && isSameDay(new Date(hour.date), new Date(selectedDate));
      if (!sameDay) return false;

      if (event?.module === "report" && hour?.report?.id && event?.module_id) {
        return hour.report.id === event.module_id;
      }

      if (event?.module === "work_order" && hour?.work_order?.id && event?.module_id) {
        return hour.work_order.id === event.module_id;
      }

      if (event?.project_id && hour?.project?.id) {
        return hour.project.id === event.project_id;
      }

      return false;
    });

    if (isUsed) {
      toast.error(t("already_registered_hours_for_event"));
      return;
    }

    const nextPrefill = await buildHourPrefillFromEvent(event, selectedDate, currentUser?.id);
    setPrefill(nextPrefill);
    setHourFormId(null);
    setDialogOpen(true);
  };

  function reportFromEvent(event) {
    if (!event || event.module !== "report" || !event.module_id) return null;

    return {
      ...(event.module_details || {}),
      id: event.module_id,
      displayTitle: event?.module_details?.displayTitle || event.title,
      report_type_id:
        event?.module_details?.report_type_id ||
        event?.module_details?.report_type?.id ||
        event?.module_details?.reportType?.id,
    };
  }

  async function openReportGuide(event, options = {}) {
    const report = reportFromEvent(event);
    if (!report?.id) return;

    const templates = await fetchReportGuideTemplates({
      report_type_id: report.report_type_id,
      purpose: "field_report",
    });
    const availableTemplates = excludingPromangStockReportGuideTemplates(
      Array.isArray(templates) ? templates.filter((item) => item?.id) : []
    );
    if (availableTemplates.length === 0) {
      if (!options.silent) toast.error(t("Ingen rapportguide er tilgjengelig"));
      return;
    }

    if (availableTemplates.length > 1) {
      setReportGuideChooserState({
        open: true,
        report,
        templates: availableTemplates,
      });
      return;
    }

    startReportGuide(report, availableTemplates[0]);
  }

  function startReportGuide(report, template) {
    setReportGuideState({
      open: true,
      template,
      report,
      isSaving: false,
    });
  }

  function closeReportGuideChooser() {
    setReportGuideChooserState({
      open: false,
      report: null,
      templates: [],
    });
  }

  function closeReportGuide() {
    setReportGuideState({
      open: false,
      template: null,
      report: null,
      isSaving: false,
    });
  }

  async function completeReportGuide(guideRun) {
    if (!reportGuideState.report?.id) return;

    setReportGuideState((state) => ({ ...state, isSaving: true }));
    const response = await createReportGuideRun(reportGuideState.report.id, {
      report_guide_template_id: guideRun.report_guide_template_id,
      purpose: guideRun.purpose,
      answers: guideRun.answers,
      requested_from_customer: false,
    });

    if (response?.errors) {
      toast.error(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors));
      setReportGuideState((state) => ({ ...state, isSaving: false }));
      return;
    }

    toast.success(t("Rapportguide lagret"));
    closeReportGuide();
    setRefreshTick((value) => value + 1);
  }

  const handleHourSaved = (savedHour) => {
    setLastChangedHour(savedHour);
    if (savedHour?.date) setSelectedDate(savedHour.date);
    setHourFormId(null);
    setRefreshTick((value) => value + 1);
  };

  const handleVehicleCheckin = async (vehicleId) => {
    if (!driver?.id) return;
    await driverVehicleAction({
      userId: currentUser?.id,
      vehicleId,
      action: "checkin",
    });
    setVehiclesListDialogOpen(false);
    setRefreshTick((value) => value + 1);
  };

  const handleVehicleCheckout = async () => {
    if (!driver?.id || !vehicle?.id) return;
    await driverVehicleAction({
      userId: currentUser?.id,
      vehicleId: vehicle.id,
      action: "checkout",
    });
    setRefreshTick((value) => value + 1);
  };

  const openNewHourFromNav = useCallback(() => {
    const date = todayString();
    setPrefill({
      user_id: currentUser?.id,
      date,
    });
    setHourFormId(null);
    setSelectedDate(date);
    setDialogOpen(true);
  }, [currentUser?.id]);

  const openNewHourForSelectedDate = useCallback(() => {
    setPrefill({ user_id: currentUser?.id, date: selectedDate });
    setHourFormId(null);
    setDialogOpen(true);
  }, [currentUser?.id, selectedDate]);

  const openNewEventFromNav = useCallback(() => {
    setIncidentOpen(true);
  }, []);

  const openNewAbsenceFromNav = useCallback(() => {
    const date = todayString();
    setAbsencePrefill({
      user_id: currentUser?.id,
      date_from: date,
    });
    setAbsenceDialogOpen(true);
  }, [currentUser?.id]);

  const handleAbsenceSaved = () => {
    setAbsenceDialogOpen(false);
    setAbsencePrefill(null);
    setRefreshTick((value) => value + 1);
  };

  useGeneralNavMenuPageActions({
    onNewHour: openNewHourFromNav,
    onNewEvent: openNewEventFromNav,
    onNewAbsence: openNewAbsenceFromNav,
  });

  return (
    <div className="pm-dashboard pd-theme" data-theme={settings.theme}>
      <div className="pd-container">
        <section className="pd-hero">
          <div>
            <p className="pd-eyebrow pd-date">{"// "}{new Date(`${selectedDate}T12:00:00`).toLocaleDateString(intlLocaleFor(settings.language), { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            <h1>{t("dashboardDesign.title")}</h1>
            <p className="pd-subtitle">{t(canReadEconomy ? "dashboardDesign.subtitleAdmin" : "dashboardDesign.subtitlePersonal")}</p>
          </div>
          <div className="pd-actions">
            {canUseHours && <button type="button" className="pd-button" onClick={openNewHourForSelectedDate}>{t("dashboardDesign.registerHours")}</button>}
            {canCreateWorkOrder ? <button type="button" className="pd-button pd-primary" onClick={() => navigate("/dashboard/work_orders/new")}><PlusCircleIcon />{t("dashboardDesign.newWorkOrder")}</button> : <RoleOnly role="hse_ks_events" action="create"><button type="button" className="pd-button pd-primary" onClick={openNewEventFromNav}><PlusCircleIcon />{t("dashboardDesign.newIncident")}</button></RoleOnly>}
          </div>
        </section>
        <section className="pd-kpis" aria-label={t("dashboardDesign.keyFigures")}>
          <div className="pd-kpi pd-kpi-featured"><span className="pd-eyebrow">{t("dashboardDesign.assignments")}</span><strong>{eventsState.isLoading || eventsState.error ? "—" : visibleEvents.length}</strong><span>{t("dashboardDesign.plannedHours", { hours: eventsState.isLoading || eventsState.error ? "—" : plannedHoursToday.toFixed(1) })}</span></div>
          <div className="pd-kpi"><span className="pd-eyebrow">{t("dashboardDesign.service")}</span><strong>{eventsState.isLoading || eventsState.error ? "—" : reportCount}</strong><span>{t("dashboardDesign.yourDay")}</span></div>
          <div className="pd-kpi"><span className="pd-eyebrow">{t("dashboardDesign.workOrders")}</span><strong>{eventsState.isLoading || eventsState.error ? "—" : workOrderCount}</strong><span>{t("dashboardDesign.yourDay")}</span></div>
          {canUseHours && <div className="pd-kpi"><span className="pd-eyebrow">{t("dashboardDesign.hours")}</span><strong>{hoursState.loading || hoursState.error ? "—" : totalHoursToday.toFixed(1)} <small>t</small></strong><span>{t("dashboardDesign.yourHours")}</span></div>}
        </section>
        <DashboardSetupOverview key={`${currentCompany?.id}-${currentUser?.id}`} />
        <div className="pd-columns">
          <div className="pd-main-column">
            <section className="pd-card pd-agenda">
              <div className="border-b border-slate-200 px-4 py-4 sm:px-6 sm:py-6">
                <div className="flex flex-col gap-3 sm:gap-5 md:flex-row md:items-end md:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600 sm:px-3 sm:py-1 sm:text-xs">
                      <CalendarDaysIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {t("dashboardDesign.dayOverview")}
                    </div>
                    <h2 className="mt-2 text-lg font-semibold tracking-tight text-slate-950 sm:mt-3 sm:text-2xl">
                      {t("dashboardDesign.dayTasks")}
                    </h2>
                    <p className="mt-1 hidden text-sm text-slate-600 sm:block">
                      {t("dashboardDesign.agendaDescription")}
                    </p>
                  </div>

                  <div className="rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600 sm:rounded-2xl sm:px-4 sm:py-3 sm:text-sm">
                    <div className="font-semibold text-slate-900">
                      {new Date(`${selectedDate}T12:00:00`).toLocaleDateString(intlLocaleFor(settings.language), { weekday: "long", day: "numeric", month: "long" })}
                    </div>
                    <div className="mt-0.5 sm:mt-1">
                      {t("dashboardDesign.daySummary", { count: eventsState.isLoading || eventsState.error ? "—" : visibleEvents.length, hours: eventsState.isLoading || eventsState.error ? "—" : plannedHoursToday.toFixed(1) })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="pd-agenda-filters" role="group" aria-label={t("dashboardDesign.filterDay")}>
                {[["all", "all"], ["work_order", "workOrders"], ["report", "service"]].map(([value, key]) => (
                  <button type="button" key={value} aria-pressed={dayFilter === value} onClick={() => setDayFilter(value)}>{t(`dashboardDesign.${key}`)}</button>
                ))}
                <input type="date" aria-label={t("dashboardDesign.date")} value={selectedDate} onChange={(event) => { if (event.target.value) setSelectedDate(event.target.value); }} />
              </div>
              {eventsState.error ? (
                <div className="pd-load-error" role="alert">{t("dashboardDesign.eventsError")} <button type="button" onClick={() => setRefreshTick((v) => v + 1)}>{t("dashboardDesign.retry")}</button></div>
              ) : eventsState.isLoading ? (
                <div className="px-6 py-12 text-sm text-slate-500">{t("dashboardDesign.loading")}</div>
              ) : filteredEvents.length === 0 ? (
                <div className="px-6 py-12">
                  <div className="pd-empty">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
                      <BriefcaseIcon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-slate-900">
                      {t(dayFilter === "all" ? "dashboardDesign.noEvents" : "dashboardDesign.noFilterEvents")}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500">
                      {t("dashboardDesign.calendarHint")}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {filteredEvents.map((event) => {
                    const metaRows = getEventMetaRows(event);

                    return (
                      <div key={event.id} className="pd-agenda-item px-4 py-4 sm:px-6 sm:py-5">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                          <Link
                            to={eventPath(event)}
                            className={`inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${getEventColor(
                              event.module
                            )}`}
                          >
                            {getEventIcon(event.module)}
                          </Link>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                {moduleLabels[event.module] || event.module}
                              </span>
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                                {getEventTimeLabel(event, selectedDate)}
                              </span>
                            </div>

                            <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-2">
                              <Link
                                to={eventPath(event)}
                                className="pd-agenda-title min-w-0 flex-1 text-lg font-semibold text-slate-950"
                              >
                                {event?.module_details?.displayTitle || event.title}
                              </Link>
                              <EventStatusChips event={event} />
                            </div>

                            {event?.description || event?.module_details?.description ? (
                              <div className="pd-agenda-description mt-2 text-sm leading-6 text-slate-600 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:m-0 [&_ul]:list-disc [&_ul]:pl-5">
                                {parse(sanitizeHtml(event?.module_details?.description || event?.description || ""))}
                              </div>
                            ) : null}

                            {metaRows.length > 0 ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {metaRows.map((item) => (
                                  <span
                                    key={`${event.id}-${item}`}
                                    className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600"
                                  >
                                    {item}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex w-full flex-col gap-3 lg:w-56">
                            <div className="rounded-2xl bg-slate-50 px-4 py-3">
                              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                <CalendarDaysIcon className="h-4 w-4" />
                                {t("Planned time")}
                              </div>
                              <div className="mt-2 text-sm font-semibold text-slate-900">
                                {getPlannedHours(event, selectedDate).toFixed(1)} t
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              <ReportHoursTag event={event} className="min-w-0 flex-1" />
                              <ItemListProductStatusTag event={event} />
                              {event.module === "report" ? (
                                <button
                                  type="button"
                                  onClick={() => openReportGuide(event)}
                                  className="pd-agenda-pill pd-agenda-pill--accent"
                                >
                                  Guide
                                </button>
                              ) : null}
                              {canUseHours && <button
                                type="button"
                                onClick={() => onSelectEvent(event)}
                                className="pd-agenda-pill"
                              >
                                {t("dashboardDesign.hours")}
                              </button>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="border-t border-slate-200 px-4 py-4 sm:px-6 sm:py-5">
                <Link
                  to={`/dashboard/calendar?view=day&date=${selectedDate}`}
                  className="pd-agenda-more inline-flex items-center gap-2 text-sm font-semibold text-slate-900"
                >
                  {t("dashboardDesign.viewCalendar")}
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </section>

            <DashboardFollowup key={`${currentCompany?.id}-${currentUser?.id}`} />
            <RoleOnly role="admin"><AccountingSummarySection dashboardDesign key={currentCompany?.id} /></RoleOnly>
          </div>
          <aside className="pd-side-column">
            <RoleOnly role="hours">
              <section className="pd-card pd-hours">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {t("dashboardDesign.timeRegistration")}
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      {t("dashboardDesign.hoursHint")}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={openNewHourForSelectedDate}
                    className="pd-button pd-primary"
                  >
                    {t("dashboardDesign.openHours")}
                  </button>
                </div>

                <DashboardWeekHours userId={currentUser?.id} companyId={currentCompany?.id} selectedDate={selectedDate} onSelectDate={setSelectedDate} refreshTick={refreshTick} />
                <div className="mt-5 rounded-2xl bg-slate-50 px-4 py-4">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        {t("dashboardDesign.registeredDay")}
                      </div>
                      <div className="mt-2 text-3xl font-semibold text-slate-950">
                        {hoursState.loading || hoursState.error ? "—" : totalHoursToday.toFixed(1)}h
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      <div>{t("dashboardDesign.hourLines", { count: hoursState.loading || hoursState.error ? "—" : dayHours.length })}</div>
                      <div>{t("dashboardDesign.plannedHours", { hours: eventsState.isLoading || eventsState.error ? "—" : plannedHoursToday.toFixed(1) })}</div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {hoursState.error ? (
                      <div role="alert" className="pd-load-error">{t("dashboardDesign.hoursError")} <button type="button" onClick={() => setRefreshTick((v) => v + 1)}>{t("dashboardDesign.retry")}</button></div>
                    ) : hoursState.loading ? <p role="status">{t("dashboardDesign.loading")}</p> : (dayHours || []).length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm text-slate-500">
                        {t("dashboardDesign.emptyHours")}
                      </div>
                    ) : (
                      dayHours.map((hour) => (
                        <button
                          type="button"
                          key={hour.id}
                          onClick={() => {
                            setHourFormId(hour.id);
                            setPrefill(null);
                            setDialogOpen(true);
                          }}
                          className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900">
                              {hour.description || t("Time Registration")}
                            </div>
                            <div className="truncate text-xs text-slate-500">
                              {hour.project?.displayTitle ||
                                hour.activity?.displayTitle ||
                                t("dashboardDesign.noProject")}
                            </div>
                          </div>
                          <div className="ml-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                            {formatHours(hour.time)}h
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </section>
            </RoleOnly>
            <HomeStatusComposer dashboardDesign />
            <SideSection
                title={t("dashboardDesign.handbook")}
                description={t("dashboardDesign.handbookHint")}
              >
                <HomeReadingCard reading={myReading} onOpen={(url) => navigate(url)} />
                <ActionButton
                  onClick={() => navigate("/dashboard/quality/employee-handbook")}
                  icon={BookOpenIcon}
                  title={t("dashboardDesign.openHandbook")}
                  subtitle={t("dashboardDesign.handbookDetails")}
                />
                <ActionButton
                  onClick={() => setColleaguesOpen(true)}
                  icon={UsersIcon}
                  title={t("dashboardDesign.colleagues")}
                  subtitle={t("dashboardDesign.colleagueDetails")}
                />
                <ActionButton
                  onClick={() => setToolsOpen(true)}
                  icon={WrenchScrewdriverIcon}
                  title={t("dashboardDesign.tools")}
                  subtitle={t("dashboardDesign.toolsDetails")}
                />
                <ActionButton
                  onClick={() => setIncidentOpen(true)}
                  icon={PlusCircleIcon}
                  title={t("dashboardDesign.incident")}
                  subtitle={t("dashboardDesign.incidentDetails")}
                />
                <div>
                  <ActionButton
                    onClick={openNewAbsenceFromNav}
                    icon={UserMinusIcon}
                    title={t("generalNavMenu.newAbsence")}
                    subtitle={t("dashboard_report.handbook_register_absence_subtitle")}
                  />
                </div>
              </SideSection>

            <RoleOnly role="vehicles" action="read">
              <SideSection
                title={t("dashboardDesign.driving")}
                description={t("dashboardDesign.drivingHint")}
              >
                <ActionButton
                  onClick={() => setVehicleDialogOpen(true)}
                  icon={TruckIcon}
                  title={
                    driverState.isLoading
                      ? t("dashboardDesign.loadingVehicle")
                      : vehicle?.number_plate || (driver?.id ? t("dashboardDesign.noVehicle") : t("dashboardDesign.noDriver"))
                  }
                  subtitle={
                    vehicle?.id
                      ? t("dashboardDesign.distanceToday", { distance: vehicle.distance_driven_today || 0 })
                      : driver?.id
                      ? t("dashboardDesign.selectVehicle")
                      : t("dashboardDesign.noDriverProfile")
                  }
                />
              </SideSection>
            </RoleOnly>

            {isPhoneroConfigured ? (
              <RoleOnly role="phone" action="read">
                <CurrentPhoneSection />
              </RoleOnly>
            ) : null}

            <RoleOnly role="admin">
              <SideSection
                title={t("dashboardDesign.admin")}
                description={t("dashboardDesign.adminHint")}
              >
                <ActionLink
                  to="/dashboard/users"
                  icon={UserGroupIcon}
                  title={t("dashboardDesign.employees")}
                  subtitle={t("dashboardDesign.employeesHint")}
                />
              </SideSection>
            </RoleOnly>
          </aside>
        </div>

        <PMDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setPrefill(null);
            setHourFormId(null);
          }}
          size="5xl"
          dense
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 sm:px-0">
              <HourForm
                key={
                  hourFormId
                    ? String(hourFormId)
                    : `new-${prefill?.report_id || prefill?.work_order_id || prefill?.project_id || prefill?.project_activity_id || selectedDate}`
                }
                showQuickHourSelection={true}
                hourId={hourFormId}
                onChange={handleHourSaved}
                prefill={prefill}
                onDateChange={(date) => {
                  setSelectedDate(date);
                  setRefreshTick((value) => value + 1);
                }}
                onClose={() => {
                  setDialogOpen(false);
                  setPrefill(null);
                  setHourFormId(null);
                  setRefreshTick((value) => value + 1);
                }}
                renderAfterDateTime={() => {
                  if (!selectedDate || eventsState.isLoading) return null;
                  if (visibleEvents.length === 0) return null;

                  return (
                    <div className="mt-2">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {t("dashboardDesign.dayAssignments")}
                      </div>
                      <div className="flex gap-2 overflow-x-auto py-1">
                        {visibleEvents.map((event) => (
                          <button
                            key={event.id}
                            type="button"
                            onClick={() => onSelectEvent(event)}
                            className="shrink-0 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                          >
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                              {moduleLabels[event.module] || event.module}
                            </div>
                            <div className="mt-1 max-w-[220px] truncate text-sm font-semibold text-slate-900">
                              {event?.module_details?.displayTitle || event.title}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {getEventTimeLabel(event, selectedDate)}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </PMDialog>

        <PMDialog
          open={absenceDialogOpen}
          onClose={() => {
            setAbsenceDialogOpen(false);
            setAbsencePrefill(null);
          }}
          size="5xl"
          dense
          panelClassName="!max-w-[720px] md:!max-w-[720px] lg:!max-w-[720px]"
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 sm:px-0">
              <AbsenceForm
                absenceId={null}
                prefill={absencePrefill}
                showHeader
                onChange={handleAbsenceSaved}
                onClose={() => {
                  setAbsenceDialogOpen(false);
                  setAbsencePrefill(null);
                }}
              />
            </div>
          </div>
        </PMDialog>

        <PMDialog
          open={reportGuideChooserState.open}
          onClose={closeReportGuideChooser}
          title={t("Velg rapportguide")}
          size="3xl"
        >
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {t("Velg guiden som passer best med oppdraget.")}
            </p>
            {(reportGuideChooserState.templates || []).map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  const report = reportGuideChooserState.report;
                  closeReportGuideChooser();
                  startReportGuide(report, template);
                }}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left transition hover:border-sky-300 hover:bg-sky-50"
              >
                <div className="text-sm font-semibold text-slate-950">
                  {template.title || t("Rapportguide")}
                </div>
                {template.description ? (
                  <div className="mt-1 text-sm text-slate-500">{template.description}</div>
                ) : null}
                <div className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                  {(template.questions || []).length} {t("spørsmål")}
                </div>
              </button>
            ))}
          </div>
        </PMDialog>

        <ReportGuideOnboardingDialog
          open={reportGuideState.open}
          template={reportGuideState.template}
          report={reportGuideState.report}
          isSaving={reportGuideState.isSaving}
          onClose={closeReportGuide}
          onComplete={completeReportGuide}
        />

        <PMDialog
          open={colleaguesOpen}
          onClose={() => setColleaguesOpen(false)}
          title={t("dashboardDesign.colleagues")}
          size="3xl"
        >
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-600">
              Ansatte og intern kontaktinformasjon.
            </div>
            {colleaguesState.isLoading ? (
              <div className="text-sm text-slate-500">Laster kollegaer...</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {colleaguesState.data.map((user) => {
                  const nextOfKin = Array.isArray(user.user_next_of_kins)
                    ? user.user_next_of_kins[0]
                    : null;
                  const contact = resolveColleagueContact(user);
                  const phoneNumber = contact.phone;
                  const email = contact.email;

                  return (
                    <div
                      key={user.id}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-4"
                    >
                      <div className="text-sm font-semibold text-slate-900">
                        {user.displayTitle || user.name}
                      </div>
                      <div className="mt-3 space-y-1 text-xs text-slate-600">
                        <div className="break-all">
                          E-post: {email || "Ikke registrert"}
                        </div>
                        <div className="break-all">
                          Telefon: {phoneNumber || "Ikke registrert"}
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {phoneNumber ? (
                          <a
                            href={`tel:${phoneNumber}`}
                            className="inline-flex items-center rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                          >
                            Ring
                          </a>
                        ) : null}
                        {email ? (
                          <a
                            href={`mailto:${email}`}
                            className="inline-flex items-center rounded-full bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800"
                          >
                            E-post
                          </a>
                        ) : null}
                      </div>

                      <div className="mt-4 rounded-xl bg-slate-50 px-3 py-3">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {"P\u00e5r\u00f8rende"}
                        </div>
                        {nextOfKin ? (
                          <div className="mt-2 space-y-1 text-xs text-slate-600">
                            <div>Navn: {nextOfKin.name || "Ikke registrert"}</div>
                            <div>
                              Relasjon: {nextOfKin.relationship_type || "Ikke registrert"}
                            </div>
                            <div>
                              Telefon: {nextOfKin.phone_number || "Ikke registrert"}
                            </div>
                            {nextOfKin.address ? <div>Adresse: {nextOfKin.address}</div> : null}
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-slate-500">
                            {"Ingen p\u00e5r\u00f8rende registrert"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </PMDialog>

        <PMDialog
          open={incidentOpen}
          onClose={() => setIncidentOpen(false)}
          dense
          size="7xl"
        >
          <HseEventForm
            onSubmit={() => {
              setIncidentOpen(false);
              toast.success("Hendelse opprettet");
            }}
            onCancel={() => setIncidentOpen(false)}
          />
        </PMDialog>

        <EmployeeToolsDialog
          open={toolsOpen}
          onClose={() => setToolsOpen(false)}
          userId={currentUser?.id}
        />

        <PMDialog
          open={vehicleDialogOpen}
          onClose={() => setVehicleDialogOpen(false)}
          title={t("dashboardDesign.driving")}
          size="3xl"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  I dag
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-950">
                  {vehicle?.distance_driven_today || 0} km
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Denne uken
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-950">
                  {vehicle?.distance_driven_this_week || 0} km
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Denne måneden
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-950">
                  {vehicle?.distance_driven_this_month || 0} km
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
              <div className="text-sm font-semibold text-slate-900">
                {vehicle?.number_plate || t("dashboardDesign.noVehicle")}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {driver?.id
                  ? vehicle?.id
                    ? "Du er innlogget på kjøretøyet under."
                    : "Velg et kjøretøy for å starte registrering."
                  : "Ingen førerprofil tilgjengelig."}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {vehicle?.id ? (
                <>
                  <button
                    type="button"
                    onClick={handleVehicleCheckout}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    Sjekk ut
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setVehicleDialogOpen(false);
                      navigate(`/dashboard/driving/vehicles/${vehicle.id}/handbook`);
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    <BookOpenIcon className="h-4 w-4" />
                    Åpne kjørebok
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setVehiclesListDialogOpen(true)}
                  disabled={!driver?.id}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                  Velg kjøretøy
                </button>
              )}
            </div>
          </div>
        </PMDialog>

        <VehiclesListDialog
          open={vehiclesListDialogOpen}
          onClose={() => setVehiclesListDialogOpen(false)}
          checkInMode
          filters={checkInVehicleFilters}
          onSelect={handleVehicleCheckin}
        />
      </div>
    </div>
  );
};
